import React, { useState } from 'react'
import QRScanner from '../../../../components/qrScanner/QRScanner'
import GuestTouChart from './GuestTouChart'
import { useChargeSession } from './useChargeSession'
import ChargeLoadingCard from './ChargeLoadingCard'
import OutletSelectionCard from './OutletSelectionCard'

const QrIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <path d="M14 14h3v3h-3z" />
        <path d="M20 14v3M14 20h3M20 20h1" />
    </svg>
)

const RATE_KEYS = ['J-Plug', 'L1', 'L2']

const getKwValues = (chargerPowerType) => ({
    'J-Plug': chargerPowerType === '3P-208' ? 9.98 : 11.52,
    L1: 1.9,
    L2: chargerPowerType === '3P-208' ? 3.3 : 3.8,
})

const RatesTable = ({ chargerPowerType, activeKey, onSelect }) => {
    const kwValues = getKwValues(chargerPowerType)
    return (
        <div
            className="gc-rates-table"
            role="table"
            aria-label="Available charger speeds"
        >
            {RATE_KEYS.map((label) => {
                const isActive = label === activeKey
                return (
                    <button
                        key={label}
                        type="button"
                        className={`gc-rate-cell${isActive ? ' is-active' : ''}`}
                        role="cell"
                        onClick={() => onSelect && onSelect(label)}
                        aria-pressed={isActive}
                    >
                        <div className="gc-rate-label">{label}</div>
                        <div className="gc-rate-value">
                            <span className="num">{kwValues[label]}</span>
                            <span className="unit">kW</span>
                        </div>
                    </button>
                )
            })}
        </div>
    )
}

const QRChargeComponent = ({
    userVehicle,
    guestChargers,
    canUseSharedCharger,
    smartOutlets,
    costData,
    costLoading,
    costError,
    powerType,
    buttonText = 'Start charge',
    onChargeStarted,
    onError,
}) => {
    const [selectedRate, setSelectedRate] = useState(
        RATE_KEYS.includes(powerType) ? powerType : 'J-Plug'
    )
    const {
        openQR,
        isRequestingCharge,
        isAuthorizing,
        pendingGPEVScan,
        selectedOutlet,
        setSelectedOutlet,
        setPendingGPEVScan,
        handleStartCharge,
        handleCloseQR,
        handleDetected,
        handleConfirmGPEV,
    } = useChargeSession({
        userVehicle,
        guestChargers,
        canUseSharedCharger,
        smartOutlets,
        onChargeStarted,
        onError,
    })

    if (isRequestingCharge) {
        return (
            <ChargeLoadingCard
                title="We're requesting your charge"
                subtitle="This may take a few moments…"
            />
        )
    }

    if (isAuthorizing) {
        return (
            <ChargeLoadingCard
                title="Verifying access"
                subtitle="Checking your charging authorization…"
            />
        )
    }

    if (pendingGPEVScan) {
        return (
            <OutletSelectionCard
                costData={costData}
                costLoading={costLoading}
                costError={costError}
                powerType={powerType}
                selectedOutlet={selectedOutlet}
                onOutletChange={setSelectedOutlet}
                onConfirm={handleConfirmGPEV}
                onCancel={() => setPendingGPEVScan(null)}
            />
        )
    }

    return (
        <div className="gc-card">
            <h1 className="gc-page-title">Guest charging</h1>
            <p className="gc-page-sub">
                Scan the QR code on the EV charger to start a session.
            </p>

            <button
                type="button"
                className="gc-btn gc-btn-primary"
                onClick={handleStartCharge}
                aria-label="Scan QR code to start charging"
            >
                <QrIcon />
                {buttonText}
            </button>

            <div className="gc-section-h">Charging rates</div>
            <RatesTable
                chargerPowerType={powerType}
                activeKey={selectedRate}
                onSelect={setSelectedRate}
            />

            <div className="gc-section-h">Today's rates</div>
            <GuestTouChart costData={costData} powerType={selectedRate} useUpCharge />
            {costLoading && (
                <div
                    style={{
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                        textAlign: 'center',
                        padding: '12px 0',
                    }}
                >
                    Loading rates…
                </div>
            )}
            {costError && (
                <div
                    style={{
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-danger)',
                        textAlign: 'center',
                        padding: '12px 0',
                    }}
                >
                    Couldn't load today's rates.
                </div>
            )}

            <p className="gc-foot">
                Rates shown for the current property.
                <br />
                By starting a session you agree to the rates above.
            </p>

            <QRScanner
                open={openQR}
                onClose={handleCloseQR}
                onDetected={handleDetected}
            />
        </div>
    )
}

export default QRChargeComponent
